import {useMemo, useState} from "react";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {TFunction} from "i18next";
import {useAtom, useAtomValue, useSetAtom} from "jotai";
import _ from "lodash";
import dayjs from "dayjs";
import {Alert, Box, Button, IconButton, Stack, Theme, Typography} from "@mui/material";
import {
    MRT_ColumnDef,
    MRT_ColumnSizingState,
    MRT_ShowHideColumnsButton,
    MRT_Updater,
    MRT_VisibilityState,
    MaterialReactTable,
    useMaterialReactTable,
} from "material-react-table";
import AddIcon from "@mui/icons-material/AddOutlined";
import {type User, UserGlobalRole} from "../../types/domain/user";
import {usersAtom, usersLoadable} from "../../store/usersStore";
import {PostUserResponseType} from "../../services/userService";
import {getUserFullName} from "../../utils/userUtils";
import {UserEditDialog} from "../dialogs/UserEditDialog";
import {EditIcon} from "../icons/EditIcon";
import {getDefaultMRTOptions} from "../base/BaseTable";
import {adminUsersTableColumnsSizeAtom, adminUsersTableColumnsVisibilityAtom} from "../../store/adminUsersTableStore";
import {ALL, BOOLEAN_OPTIONS, USER_GLOBAL_ROLES, enumItemToTableFilterSelectOption} from "../../utils/enumItemsUtils";
import {useLocalizedEnumItems} from "../../hooks/localizedEnumItemsHooks";
import {TableFilterSelectOption} from "../../types/table";
import {authenticatedUserTableLocaleAtom} from "../../store/authenticatedUserStore";
import {formatShortDate} from "../../utils/dateUtils";
import {useUserPreferences} from "../../hooks/userPreferencesHooks";
import {useCompanies} from "../../hooks/companiesHooks";

export const AdminUsersView: React.FC = () => {
    const {t} = useTranslation();
    const {dateFormat} = useUserPreferences();
    const usersState = useAtomValue(usersLoadable);
    const refreshUsers = useSetAtom(usersAtom);
    const companies = useCompanies();
    const [activeUser, setActiveUser] = useState<User | undefined>(undefined);
    const [userCreateDialogOpened, setUserCreateDialogOpened] = useState<boolean>(false);
    const [userEditDialogOpened, setUserEditDialogOpened] = useState<boolean>(false);
    const [createdUsersAlerts, setCreatedUsersAlerts] = useState<PostUserResponseType[]>([]);
    const [adminUsersTableColumnsVisibility, setAdminUsersTableColumnsVisibility] = useAtom(
        adminUsersTableColumnsVisibilityAtom,
    );
    const [locale] = useAtom(authenticatedUserTableLocaleAtom);
    const defaultTableOptions = getDefaultMRTOptions<User>(locale);
    const [adminUsersTableColumnsSize, setAdminUsersTableColumnsSize] = useAtom(adminUsersTableColumnsSizeAtom);
    const {items: apiEnabledOptions} = useLocalizedEnumItems(API_ENABLED_OPTIONS);
    const columns = useMemo(
        () =>
            getTableColumns(
                t,
                apiEnabledOptions.map(enumItemToTableFilterSelectOption),
                ROLES_OPTIONS.map(enumItemToTableFilterSelectOption),
                dateFormat,
            ),
        [t, apiEnabledOptions, dateFormat],
    );

    const onUserCreateDialogClose = (isSuccessful: boolean, userResponse?: PostUserResponseType) => {
        setUserCreateDialogOpened(false);
        if (isSuccessful) {
            if (userResponse) {
                setCreatedUsersAlerts([...createdUsersAlerts, userResponse]);
            }
            refreshUsers();
        }
    };

    const onUserEditDialogClose = (isSuccessful: boolean) => {
        setUserEditDialogOpened(false);
        setActiveUser(undefined);
        if (isSuccessful) {
            refreshUsers();
        }
    };

    const onColumnVisibilityChange = (v: MRT_VisibilityState | MRT_Updater<MRT_VisibilityState>) => {
        setAdminUsersTableColumnsVisibility(v);
    };

    const onColumnSizingChange = (v: MRT_ColumnSizingState | MRT_Updater<MRT_ColumnSizingState>) => {
        setAdminUsersTableColumnsSize(v as Record<string, number>);
    };

    const table = useMaterialReactTable({
        ...defaultTableOptions, //spread your default options

        muiTableContainerProps: {
            sx: {
                maxHeight: "calc(100vh - 12rem)",
                minHeight: "calc(100vh - 12rem)",
            },
        },
        initialState: {
            ...defaultTableOptions.initialState,
            columnPinning: {
                right: ["mrt-row-actions"],
            },
            pagination: {
                pageIndex: 0,
                pageSize: 20,
            },
            columnFilters: [
                {
                    id: "role",
                    value: "ALL",
                },
                {
                    id: "isApiEnabled",
                    value: "ALL",
                },
            ],
        },
        state: {
            isLoading: companies.isLoading,
            columnVisibility: adminUsersTableColumnsVisibility,
            ...(adminUsersTableColumnsSize && {columnSizing: adminUsersTableColumnsSize}),
        },
        columns,
        data: usersState.state === "hasData" ? usersState.data : [],
        muiTableBodyCellProps: {
            sx: {
                whiteSpace: "nowrap",
                paddingTop: "0.5rem",
                paddingBottom: "0.5rem",
            },
        },
        enableColumnResizing: true,
        onColumnVisibilityChange: onColumnVisibilityChange,
        onColumnSizingChange: onColumnSizingChange,
        displayColumnDefOptions: {
            "mrt-row-actions": {
                enableResizing: false,
                size: 30,
                header: "",
            },
        },
        muiTableHeadRowProps: () => ({sx: {backgroundColor: "#F3F5FA"}}),
        renderRowActions: ({row}) => (
            <Box sx={{display: "flex", gap: "1rem"}}>
                <IconButton
                    color="primary"
                    onClick={() => {
                        setActiveUser(row.original);
                        setUserEditDialogOpened(true);
                    }}
                >
                    <EditIcon
                        sx={{
                            stroke: "#303A8C",
                            width: "1.125rem",
                            height: "1.125rem",
                        }}
                    />
                </IconButton>
            </Box>
        ),
    });

    return (
        <Stack paddingX="1.5rem" paddingBottom="0.5rem">
            <Stack direction="row" height="4rem" alignItems="center" justifyContent="space-between">
                <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                    {t("admin.users_view.title")}
                </Typography>
                <Stack direction="row">
                    <Button startIcon={<AddIcon />} onClick={() => setUserCreateDialogOpened(true)}>
                        {t("admin.users_view.create_button")}
                    </Button>
                    <MRT_ShowHideColumnsButton table={table} />
                </Stack>
            </Stack>
            {createdUsersAlerts.map((user) => (
                <Alert
                    key={user.id}
                    severity="success"
                    variant="filled"
                    color="info"
                    sx={{mt: "1.25rem"}}
                    onClose={() => {
                        setCreatedUsersAlerts(_.filter(createdUsersAlerts, (x) => x.id !== user.id));
                    }}
                >
                    {t("admin.users_view.user_created", {name: getUserFullName(user)})}
                    {user.password !== undefined && (
                        <>
                            {t("admin.users_view.user_credentials", {
                                email: user.properties.email,
                                password: user.password,
                            })}
                        </>
                    )}
                </Alert>
            ))}
            <MaterialReactTable table={table} />
            {userCreateDialogOpened && (
                <UserEditDialog mode="create" open={userCreateDialogOpened} onClose={onUserCreateDialogClose} />
            )}
            {userEditDialogOpened && activeUser !== undefined && (
                <UserEditDialog
                    mode="edit"
                    user={activeUser}
                    open={userEditDialogOpened}
                    onClose={onUserEditDialogClose}
                />
            )}
        </Stack>
    );
};

const API_ENABLED_OPTIONS = [ALL, ...BOOLEAN_OPTIONS];
const ROLES_OPTIONS = [ALL, ...USER_GLOBAL_ROLES];

const getTableColumns = (
    t: TFunction<string, undefined>,
    apiEnabledOptions: TableFilterSelectOption[],
    rolesOptions: TableFilterSelectOption[],
    dateFormat: string,
): MRT_ColumnDef<User>[] => [
    {
        id: "name",
        header: t("admin.users_view.columns.name"),
        accessorFn: (row) => `${row.properties.firstName} ${row.properties.lastName}`,
        filterFn: "contains",
        Cell: ({renderedCellValue, row}) => <Link to={`/user/${row.original.id}`}>{renderedCellValue}</Link>,
        minSize: 100,
    },
    {
        id: "id",
        header: t("admin.users_view.columns.id"),
        accessorKey: "id",
        filterFn: "contains",
        minSize: 100,
    },
    {
        id: "email",
        header: t("admin.users_view.columns.email"),
        accessorKey: "properties.email",
        filterFn: "contains",
        minSize: 100,
    },
    {
        id: "company",
        header: t("admin.users_view.columns.company"),
        accessorKey: "properties.company",
        filterFn: "contains",
        minSize: 100,
    },
    {
        id: "role",
        header: t("admin.users_view.columns.role"),
        accessorKey: "security.role",
        minSize: 80,
        size: 80,
        filterVariant: "select",
        filterSelectOptions: rolesOptions,
        filterFn: (row, _, filter) => filter === "ALL" || row.original.security.role === filter,
        Cell: ({cell}) => (
            <Box
                component="span"
                sx={(theme) => ({
                    backgroundColor: globalRoleToBgColorMap[cell.getValue<UserGlobalRole>()](theme),
                    borderRadius: "0.25rem",
                    color: "#fff",
                    padding: "0.50rem",
                    letterSpacing: ".04em",
                })}
            >
                {cell.getValue<string>()}
            </Box>
        ),
    },
    {
        id: "country",
        header: t("admin.users_view.columns.country"),
        accessorKey: "properties.country",
        filterFn: "contains",
        minSize: 100,
    },
    {
        id: "phone",
        header: t("admin.users_view.columns.phone"),
        accessorKey: "properties.phone",
        filterFn: "contains",
        minSize: 100,
    },
    {
        id: "isApiEnabled",
        header: t("admin.companies_view.columns.api_enabled"),
        accessorKey: "isApiEnabled",
        minSize: 80,
        size: 80,
        filterVariant: "select",
        filterSelectOptions: apiEnabledOptions,
        filterFn: (row, _, filter) => filter === "ALL" || row.original.isApiEnabled === (filter === "YES"),
        Cell: ({row}) => (row.original.isApiEnabled ? t("enumerations.boolean.yes") : t("enumerations.boolean.no")),
    },
    {
        id: "createdAt",
        header: t("admin.users_view.columns.created_at"),
        filterFn: "contains",
        accessorFn: (data: User) => formatShortDate(dayjs(data.createdAt), dateFormat),
        sortingFn: (a, b) => {
            return new Date(a.original.createdAt).valueOf() - new Date(b.original.createdAt).valueOf();
        },
        minSize: 100,
    },
    {
        id: "updatedAt",
        header: t("admin.users_view.columns.updated_at"),
        filterFn: "contains",
        accessorFn: (data: User) => formatShortDate(dayjs(data.updatedAt), dateFormat),
        sortingFn: (a, b) => {
            return new Date(a.original.updatedAt).valueOf() - new Date(b.original.updatedAt).valueOf();
        },
        minSize: 100,
    },
];

const globalRoleToBgColorMap: Record<UserGlobalRole, (theme: Theme) => string> = {
    ADMIN: (theme) => theme.palette.error.dark,
    CS: (theme) => theme.palette.warning.dark,
    HYDROSAT: (theme) => theme.palette.info.dark,
    USER: (theme) => theme.palette.success.dark,
};
